'use client'

import React from 'react'
import { format } from 'date-fns'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Calendar } from '@/shared/components/ui/calendar'
import { statusFilterWithAllOptions } from '../constants/options'

const SUGGESTIONS_MAX = 80

export function ComboBoxFilterContent({
  value,
  onApply,
  suggestions = [],
  placeholder = 'Type to search…',
}: {
  value: string
  onApply: (v: string | null) => void
  suggestions: string[]
  placeholder?: string
}) {
  const [local, setLocal] = React.useState(value)
  const [isListOpen, setIsListOpen] = React.useState(false)
  React.useEffect(() => setLocal(value), [value])
  const filtered = React.useMemo(() => {
    if (!suggestions.length) return []
    const q = local.trim().toLowerCase()
    return (q ? suggestions.filter((s) => s.toLowerCase().includes(q)) : suggestions).slice(0, SUGGESTIONS_MAX)
  }, [suggestions, local])
  const showList = isListOpen && filtered.length > 0
  const hasActive = !!value
  return (
    <div className="w-64 p-2 space-y-2">
      <Input
        value={local}
        onChange={(e) => {
          setLocal((e.target as HTMLInputElement).value)
          setIsListOpen(true)
        }}
        onFocus={() => setIsListOpen(true)}
        onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onApply(local.trim() || null))}
        placeholder={placeholder}
        className="h-8"
      />
      {showList && (
        <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
              onMouseDown={(e) => {
                e.preventDefault()
                setLocal(s)
                onApply(s)
                setIsListOpen(false)
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => onApply(local.trim() || null)}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}

export function RolesFilterContent({
  value,
  onApply,
  options,
}: {
  value: string[]
  onApply: (v: string[] | null) => void
  options: readonly { value: string; label: string }[]
}) {
  const selected = React.useMemo(() => (value?.length ? value : []), [value])
  const [local, setLocal] = React.useState<string[]>(Array.isArray(selected) ? selected.slice() : [])
  React.useEffect(() => setLocal(Array.isArray(selected) ? selected.slice() : []), [selected])
  const toggle = (v: string) => setLocal((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  const hasActive = local.length > 0
  return (
    <div className="w-56 p-2 space-y-2">
      <div className="border rounded-md bg-muted/50 max-h-48 overflow-y-auto">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
            <Checkbox checked={local.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
            <span className="text-sm">{o.label}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => onApply(local.length ? local : null)}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}

export function SingleDateFilterContent({
  value,
  onApply,
  placeholder = 'Select date',
}: {
  value: string
  onApply: (v: string | null) => void
  placeholder?: string
}) {
  const parse = (s: string) => {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
    const [y, m, d] = s.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const date = parse(value)
  const [selected, setSelected] = React.useState<Date | undefined>(date)
  React.useEffect(() => setSelected(date), [value, date])
  const hasActive = !!value
  return (
    <div className="w-auto p-2 space-y-2">
      <Calendar
        mode="single"
        selected={selected}
        onSelect={setSelected}
        defaultMonth={selected || new Date()}
      />
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => onApply(selected ? format(selected, 'yyyy-MM-dd') : null)}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}

export function RegionFilterContent({
  value,
  onApply,
  options,
}: {
  value: string
  onApply: (v: string | null) => void
  options: { value: string; label: string }[]
}) {
  const [local, setLocal] = React.useState(value)
  React.useEffect(() => setLocal(value), [value])
  const hasActive = !!value
  return (
    <div className="w-56 p-2 space-y-2">
      <div className="border rounded-md bg-muted/50 max-h-48 overflow-y-auto">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
            <Checkbox checked={local === o.value} onCheckedChange={() => setLocal(local === o.value ? '' : o.value)} />
            <span className="text-sm">{o.label}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => onApply(local || null)}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}

export function StatusFilterContent({ value, onApply }: { value: string; onApply: (v: string | null) => void }) {
  const urlVal = value === 'true' ? 'active' : value === 'false' ? 'inactive' : 'all'
  const [local, setLocal] = React.useState(urlVal)
  React.useEffect(() => setLocal(value === 'true' ? 'active' : value === 'false' ? 'inactive' : 'all'), [value])
  const hasActive = local !== 'all'
  const apply = () => onApply(local === 'all' ? null : local === 'active' ? 'true' : 'false')
  return (
    <div className="w-48 p-2 space-y-2">
      <div className="border rounded-md bg-muted/50">
        {statusFilterWithAllOptions.map((o) => (
          <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
            <Checkbox checked={local === o.value} onCheckedChange={() => setLocal(o.value)} />
            <span className="text-sm">{o.label}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={apply}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}

export function AddedFilterContent({
  addedFrom,
  addedTo,
  onApply,
}: {
  addedFrom: string
  addedTo: string
  onApply: (from: string | null, to: string | null) => void
}) {
  const parse = (s: string) => {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
    const [y, m, d] = s.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const fromDate = parse(addedFrom)
  const toDate = parse(addedTo)
  const [range, setRange] = React.useState<{ from?: Date; to?: Date } | undefined>(() =>
    fromDate || toDate ? { from: fromDate, to: toDate } : undefined
  )
  React.useEffect(() => {
    setRange(fromDate || toDate ? { from: fromDate, to: toDate } : undefined)
  }, [fromDate, toDate])
  const hasActive = !!addedFrom || !!addedTo
  const apply = () => {
    const fromStr = range?.from ? format(range.from, 'yyyy-MM-dd') : null
    const toStr = range?.to ? format(range.to, 'yyyy-MM-dd') : range?.from ? format(range.from, 'yyyy-MM-dd') : null
    onApply(fromStr, toStr)
  }
  return (
    <div className="w-auto p-2 space-y-2">
      <Calendar
        mode="range"
        selected={range === undefined ? undefined : { from: range.from, to: range.to }}
        onSelect={setRange}
        defaultMonth={range?.from || range?.to || new Date()}
        numberOfMonths={2}
      />
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={apply}>Apply</Button>
        {hasActive && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null, null)}>Clear</Button>
        )}
      </div>
    </div>
  )
}
